import { Sun, Clock, AlertTriangle, Gauge } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { cn } from '@/lib/utils';
import { useAnimatedValue } from '@/hooks/useAnimatedValue';
import type { EnergyMixSummary, SolarBatterySummary } from '@/types/energy';

interface EnergyMixCompactProps {
  /** null = no measured mix for the range (prod honest absence) */
  data: EnergyMixSummary | null;
  solarData?: SolarBatterySummary;
  animateValues?: boolean;
  className?: string;
}

/**
 * Portrait-oriented compact energy mix display with donut chart
 * Shows Solar, Grid, and Generator percentages with solar stats below
 */
export function EnergyMixCompact({ data, solarData, animateValues = false, className }: EnergyMixCompactProps) {
  // null summary (prod, no measured mix): the donut geometry zeroes out but
  // every label reads N/A/— so absence is never mistaken for a measured 0.
  const hasMix = data != null;
  const { solarKwh, gridKwh, generatorKwh, totalKwh } =
    data ?? { solarKwh: 0, gridKwh: 0, generatorKwh: 0, totalKwh: 0 };

  // Animated values for live demo mode
  const animatedSolarUtilization = useAnimatedValue(solarData?.solarUtilization || 0, { enabled: animateValues });
  const animatedBatteryAutonomy = useAnimatedValue(solarData?.avgBatteryAutonomy || 0, { enabled: animateValues });

  // Calculate percentages - generatorPercent as remainder to guarantee sum = 100%
  const solarPercent = totalKwh > 0 ? Math.round((solarKwh / totalKwh) * 100) : 0;
  const gridPercent = totalKwh > 0 ? Math.round((gridKwh / totalKwh) * 100) : 0;
  const generatorPercent = totalKwh > 0 ? Math.max(0, 100 - solarPercent - gridPercent) : 0;

  const formatKwh = (kwh: number) => {
    return Math.round(kwh).toLocaleString();
  };

  // SVG donut chart parameters - sized to fit above bottom stats
  const size = 115;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Calculate stroke dash arrays for each segment
  const solarDash = (solarPercent / 100) * circumference;
  const gridDash = (gridPercent / 100) * circumference;
  const generatorDash = (generatorPercent / 100) * circumference;

  // Calculate offsets (each segment starts where the previous ended)
  const solarOffset = 0;
  const gridOffset = -solarDash;
  const generatorOffset = -(solarDash + gridDash);

  // Calculate label positions on the arc (midpoint of each segment)
  const getLabelPosition = (startPercent: number, segmentPercent: number) => {
    const midPercent = startPercent + segmentPercent / 2;
    const angle = (midPercent / 100) * 2 * Math.PI - Math.PI / 2; // -90 deg offset for SVG rotation
    const labelRadius = radius + strokeWidth / 2 + 18; // Outside the arc
    return {
      x: center + labelRadius * Math.cos(angle),
      y: center + labelRadius * Math.sin(angle),
    };
  };

  const solarLabelPos = getLabelPosition(0, solarPercent);
  const gridLabelPos = getLabelPosition(solarPercent, gridPercent);
  const generatorLabelPos = getLabelPosition(solarPercent + gridPercent, generatorPercent);

  return (
    <Card className={cn('flex flex-col h-full relative', className)}>
      <div className="absolute top-1.5 right-2 z-10">
        <InfoTooltip
          content={
            <div className="space-y-1">
              <p className="font-semibold">Energy Mix</p>
              <p>Shows the proportion of energy from each source: solar (green), grid (orange), and generator (gray).</p>
              <p className="text-muted-foreground">Displays total consumption with breakdown by source, plus key solar system performance metrics below.</p>
            </div>
          }
        />
      </div>
      <CardContent className="flex-1 flex flex-col justify-between p-2 gap-1">
        {/* Header - top left, aligned with other top-row panels */}
        <div className="flex items-center gap-1 shrink-0">
          <Sun className="h-3.5 w-3.5 text-[#8FB560]" />
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Energy Mix
          </h3>
        </div>

        {/* Main content: Donut Chart centered in left area + Legend positioned right */}
        <div className="relative flex items-start justify-center flex-shrink-0 pr-16" style={{ height: `${size + 20}px` }}>
          {/* Donut Chart with percentage labels */}
          <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="transform -rotate-90">
              {/* Background circle */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                className="text-muted/20"
              />
              {/* Generator segment (gray) */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#6b7280"
                strokeWidth={strokeWidth}
                strokeDasharray={`${generatorDash} ${circumference - generatorDash}`}
                strokeDashoffset={generatorOffset}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
              {/* Grid segment (orange) */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#f59e0b"
                strokeWidth={strokeWidth}
                strokeDasharray={`${gridDash} ${circumference - gridDash}`}
                strokeDashoffset={gridOffset}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
              {/* Solar segment (green) */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#8FB560"
                strokeWidth={strokeWidth}
                strokeDasharray={`${solarDash} ${circumference - solarDash}`}
                strokeDashoffset={solarOffset}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
            {/* Center text - total */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-bold text-foreground">{hasMix ? formatKwh(totalKwh) : 'N/A'}</span>
              <span className="text-[10px] text-muted-foreground">{hasMix ? 'kWh' : 'no data'}</span>
            </div>
            {/* Percentage labels on segments */}
            {solarPercent > 5 && (
              <span
                className="absolute text-[10px] font-bold text-[#8FB560]"
                style={{
                  left: solarLabelPos.x,
                  top: solarLabelPos.y,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {solarPercent}%
              </span>
            )}
            {gridPercent > 5 && (
              <span
                className="absolute text-[10px] font-bold text-amber-500"
                style={{
                  left: gridLabelPos.x,
                  top: gridLabelPos.y,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {gridPercent}%
              </span>
            )}
            {generatorPercent > 5 && (
              <span
                className="absolute text-[10px] font-bold text-gray-400"
                style={{
                  left: generatorLabelPos.x,
                  top: generatorLabelPos.y,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {generatorPercent}%
              </span>
            )}
          </div>

          {/* Legend - absolute positioned right side with kWh values */}
          <div className="absolute right-2 top-6 flex flex-col gap-1 text-[10px]">
            <div className="flex items-end justify-between gap-2">
              <div className="flex items-end gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#8FB560] mb-0.5" />
                <span className="text-foreground leading-none">Solar</span>
              </div>
              <span className="font-semibold text-[#8FB560] leading-none">{hasMix ? formatKwh(solarKwh) : '—'} <span className="text-muted-foreground font-normal">kWh</span></span>
            </div>
            <div className="flex items-end justify-between gap-2">
              <div className="flex items-end gap-1.5">
                <div className="w-2 h-2 rounded-full bg-amber-500 mb-0.5" />
                <span className="text-foreground leading-none">Grid</span>
              </div>
              <span className="font-semibold text-amber-500 leading-none">{hasMix ? formatKwh(gridKwh) : '—'} <span className="text-muted-foreground font-normal">kWh</span></span>
            </div>
            <div className="flex items-end justify-between gap-2">
              <div className="flex items-end gap-1.5">
                <div className="w-2 h-2 rounded-full bg-gray-500 mb-0.5" />
                <span className="text-foreground leading-none">Generator</span>
              </div>
              <span className="font-semibold text-gray-500 leading-none">{hasMix ? formatKwh(generatorKwh) : '—'} <span className="text-muted-foreground font-normal">kWh</span></span>
            </div>
          </div>
        </div>

        {/* Solar Stats - 2x2 grid with readable text */}
        {solarData && (
          <div className="grid grid-cols-[1fr_1.3fr] gap-x-3 gap-y-1 text-xs px-3 pb-2">
            {/* Peak Output */}
            <div className="flex items-center gap-1.5">
              <Sun className="h-3.5 w-3.5 text-[#8FB560] flex-shrink-0" />
              <div>
                <span className="font-semibold text-foreground">{solarData.peakSolarOutput ?? 'N/A'}</span>
                <span className="text-muted-foreground"> kW peak</span>
              </div>
            </div>

            {/* Utilization */}
            <div className="flex items-center gap-1.5">
              <Gauge className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <div>
                <span className="font-semibold text-foreground">{solarData.solarUtilization == null ? 'N/A' : `${Math.round(animatedSolarUtilization)}%`}</span>
                <span className="text-muted-foreground"> utilized</span>
              </div>
            </div>

            {/* Battery Autonomy */}
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <div>
                <span className="font-semibold text-foreground">{solarData.avgBatteryAutonomy == null ? 'N/A' : `${Math.round(animatedBatteryAutonomy)}h`}</span>
                <span className="text-muted-foreground"> autonomy</span>
              </div>
            </div>

            {/* Red Zone Events */}
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <div className="flex items-center gap-1">
                <span className="font-semibold text-foreground">{solarData.batteryRedZoneEvents}</span>
                <span className="text-muted-foreground">red-zone events</span>
                {solarData.redZoneChangePercent !== 0 && (
                  <span className={cn(
                    'text-[10px] font-medium',
                    solarData.redZoneChangePercent < 0 ? 'text-[#8FB560]' : 'text-destructive'
                  )}>
                    {solarData.redZoneChangePercent < 0 ? '↓' : '↑'}
                    {Math.abs(solarData.redZoneChangePercent)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
